import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmt } from "@/lib/timezone";
import { useBrand } from "@/lib/brand";

const StudentDashboard = ({ user, onLogout }) => {
  const { default_timezone } = useBrand();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await api.get(`/enrollments?student_id=${user.user_id}`);
      setEnrollments(response.data.enrollments);
    } catch (error) {
      toast.error("Failed to load enrollments");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-heading text-slate-900" data-testid="student-dashboard-title">
            Welcome, {user.name}
          </h1>
          <p className="text-slate-600 mt-2">Your dance journey at a glance</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : enrollments.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
            <p className="text-slate-600">No active enrollments yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {enrollments.map((enrollment, index) => (
              <motion.div
                key={enrollment.enrollment_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
                data-testid={`enrollment-card-${index}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold font-heading text-slate-900">Batch ID: {enrollment.batch_id}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {fmt(enrollment.start_date, "dd MMM yyyy", default_timezone)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Expires: {fmt(enrollment.end_date, "dd MMM yyyy", default_timezone)}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      enrollment.status === "active"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {enrollment.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <button
          onClick={onLogout}
          data-testid="student-logout-button"
          className="mt-8 w-full md:w-auto px-6 py-3 rounded-full bg-red-500 text-white hover:bg-red-600 font-medium"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default StudentDashboard;